import { useEffect, useState } from "react";
import { ClienteSalvaSchermo } from "./ClienteSalvaSchermo";
import { Vettura } from "./ClienteEv";
import { Settings, X, Share2, Type, Gauge } from "lucide-react";

const Group = ({ title, children, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-400">{title}</h3>
    {children}
  </div>
);

// Impostazione del tetto del contatore (potenza contrattuale) direttamente dal cliente finale:
// limita la ricarica dell'auto per non far scattare il contatore. Salva a livello di impianto.
function TettoContatore({ src }) {
  const [kw, setKw] = useState("");
  const [sugg, setSugg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    src.evMeterCapGet()
      .then((d) => { setKw(d?.potenza_kw ?? ""); setSugg(d?.suggerito_da_bolletta ?? null); })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const salva = async () => {
    setSaving(true); setOk(false); setErr("");
    try {
      await src.evMeterCapSet(kw === "" ? null : Number(kw));
      setOk(true); setTimeout(() => setOk(false), 2500);
    } catch (e) { setErr(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-2xl bg-slate-50 p-3" data-testid="impost-tetto-contatore">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700"><Gauge className="h-4 w-4 text-sky-600" />Potenza del contatore</div>
      <p className="mb-2 text-[11px] text-slate-400">Indica la potenza del tuo contatore in kW (come in bolletta). Serve a limitare la ricarica dell'auto così da non far scattare il contatore. Lascia vuoto per non impostare alcun limite.</p>
      <div className="flex items-end gap-2">
        <input type="number" step="0.5" inputMode="decimal" value={kw} onChange={(e) => setKw(e.target.value)} placeholder={sugg ? String(sugg) : "es. 6"}
          className="h-10 w-28 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none focus:border-sky-400" data-testid="impost-tetto-kw" />
        <span className="pb-2.5 text-sm text-slate-500">kW</span>
        <button type="button" onClick={salva} disabled={saving || !loaded} data-testid="impost-tetto-salva"
          className="ml-auto rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-sky-700 disabled:opacity-50">{saving ? "Salvo…" : ok ? "Salvato ✓" : "Salva"}</button>
      </div>
      {sugg && !kw && <p className="mt-1.5 text-[11px] text-sky-700" data-testid="impost-tetto-sugg">Dalla bolletta risulta {sugg} kW.</p>}
      {err && <p className="mt-1.5 text-[11px] text-rose-600" data-testid="impost-tetto-err">{err}</p>}
    </div>
  );
}

// Pannello ⚙️ Impostazioni della Vista Cliente (aperto dall'ingranaggio nell'header).
// Raggruppa le impostazioni: usabilità (dimensione testo), condivisione, presentazione (Modalità Schermo,
// solo Pro) e, SOLO in anteprima staff/installatore, le impostazioni installatore (Versione Pro, avvisi, link).
export function ClienteImpostazioni({ src, imp, grande, setGrande, staff, onShare, onClose, reload }) {
  const seg = (on) => `flex-1 rounded-lg px-3 py-2 text-sm font-bold transition-colors ${on ? "bg-white text-sky-700 shadow-sm" : "text-slate-500"}`;
  // Blocca lo scroll della pagina dietro mentre il modale è aperto (evita lo scroll-chaining su iOS):
  // fissa il body salvando la posizione e la ripristina alla chiusura.
  useEffect(() => {
    const y = window.scrollY;
    const b = document.body;
    const prev = { position: b.style.position, top: b.style.top, width: b.style.width, overflow: b.style.overflow };
    b.style.position = "fixed";
    b.style.top = `-${y}px`;
    b.style.width = "100%";
    b.style.overflow = "hidden";
    return () => {
      b.style.position = prev.position;
      b.style.top = prev.top;
      b.style.width = prev.width;
      b.style.overflow = prev.overflow;
      window.scrollTo(0, y);
    };
  }, []);
  const [evStazioni, setEvStazioni] = useState(null);
  const [evSi, setEvSi] = useState(0);
  useEffect(() => {
    if (!src.ev) return;
    src.ev().then((d) => setEvStazioni(d?.stazioni || [])).catch(() => setEvStazioni([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose} data-testid="cliente-impostazioni">
      <div className="cliente-font flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white sm:max-h-[85vh] sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 p-4">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900"><Settings className="h-5 w-5 text-sky-600" />Impostazioni</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 transition-colors hover:text-slate-700" aria-label="Chiudi" data-testid="cliente-impostazioni-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain p-4" data-testid="cliente-impostazioni-body">
          <Group title="Usabilità" testid="impost-usabilita">
            <div className="rounded-2xl bg-slate-50 p-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700"><Type className="h-4 w-4 text-sky-600" />Dimensione testo</div>
              <div className="flex gap-1 rounded-xl bg-slate-200/60 p-1">
                <button type="button" className={seg(!grande)} onClick={() => setGrande(false)} data-testid="impost-testo-normale">Normale</button>
                <button type="button" className={seg(grande)} onClick={() => setGrande(true)} data-testid="impost-testo-grande">Grande</button>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">Ingrandisce tutta la Vista Cliente: utile su TV o per una lettura più comoda.</p>
            </div>
          </Group>

          {src.evMeterCapGet && (
            <Group title="Ricarica auto · Contatore" testid="impost-contatore">
              <TettoContatore src={src} />
            </Group>
          )}

          {imp?.pro && evStazioni && evStazioni.length > 0 && (
            <Group title="Ricarica auto · La tua auto" testid="impost-auto">
              {evStazioni.length > 1 && (
                <div className="flex flex-wrap gap-2" data-testid="impost-auto-stations">
                  {evStazioni.map((s, i) => (
                    <button key={s.id} type="button" onClick={() => setEvSi(i)} data-testid={`impost-auto-station-${s.id}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${i === evSi ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s.nome}</button>
                  ))}
                </div>
              )}
              <Vettura src={src} st={evStazioni[evSi]} />
            </Group>
          )}

          <Group title="Condivisione" testid="impost-condivisione">
            <button type="button" onClick={() => { onClose(); onShare(); }} data-testid="impost-condividi"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-sky-700">
              <Share2 className="h-4 w-4" />Condividi il tuo impianto
            </button>
            <p className="text-[11px] text-slate-400">Crea un link da inviare: resta visibile per 24 ore.</p>
          </Group>

          {imp.pro && src.logoUp && (
            <Group title="Presentazione · Modalità Schermo" testid="impost-presentazione">
              <ClienteSalvaSchermo src={src} hasLogo={!!imp.cliente_logo} hasVideo={!!imp.cliente_video} nome={imp.nome} logoUrl={imp.cliente_logo} logoBg={imp.cliente_logo_bg} videoUrl={src.videoUrl} bollettinoAuto={imp.bollettino_auto} onChange={reload} />
            </Group>
          )}

          {staff && (
            <Group title="Impostazioni installatore" testid="impost-installatore">
              {staff}
            </Group>
          )}
        </div>
      </div>
    </div>
  );
}
